import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, Loader2, Smartphone, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from './HelpHint';
import { api } from '../lib/api';

/**
 * The Daraja credentials for M-PESA Express.
 *
 * ## The order the fields are in, and why it is not ours to choose
 *
 * Consumer Key, Consumer Secret, Passkey, Short Code — the order the Daraja
 * app card lists them, because that is what somebody has open in another tab
 * while they fill this in. They were previously grouped by whether they were
 * secret and laid out two to a row, which is a fact about how this system
 * stores them and of no interest at all to the person copying four values
 * across. Making somebody reorder four credentials in their head is how a
 * consumer key ends up in the passkey box.
 *
 * ## The rule every credential field in this application follows
 *
 * **A secret goes in and never comes back out.** The server returns `••••7f3a`
 * and a flag saying one is stored; this page shows that mask in a disabled
 * field and only sends a value when somebody actually types a new one.
 *
 * That is why each secret has a "Change it" button rather than an editable box
 * with dots in it. An editable box pre-filled with a mask is the shape that
 * produces the classic bug: an admin opens the page to change the paybill
 * number, presses Save, and overwrites the consumer secret with four bullet
 * characters. The button makes replacing a credential a deliberate act, and
 * leaves every other field on the page safe to edit.
 *
 * ## Why there is one Save button now
 *
 * Because there was none, and nothing on the screen ever said a change had
 * been kept. Each field wrote itself away when it lost focus and each select
 * wrote itself away on change — so the page saved constantly and silently, and
 * the only way to find out whether anything had stuck was to reload and look.
 * Half-typed values were saved by tabbing past them.
 *
 * Now every control edits a draft, the button says how many changes are
 * waiting, and nothing leaves this page until it is pressed.
 *
 * ## What is not secret
 *
 * The short code and the consumer key. Both are printed on receipts or visible
 * in Safaricom's own portal, and masking them would only stop an admin
 * checking they had typed the right one.
 */

/**
 * Safaricom's one shortcode for every sandbox app.
 *
 * Not a secret and not per-account: the whole sandbox shares it, which is
 * exactly why an app card shows "Short Code: N/A" and sends people looking for
 * something that was never going to be there.
 */
const SANDBOX_SHORTCODE = '174379';

/**
 * The only handset Safaricom's sandbox will ring.
 *
 * A sandbox push aimed at a real number is accepted and then reaches nobody,
 * which looks identical to a push that failed. Worth saying out loud on the
 * screen where somebody is about to sit and watch their own phone.
 */
export const SANDBOX_TEST_MSISDN = '254708374149';

export function MpesaCredentials() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /** Which secrets the admin has chosen to replace this visit. */
  const [replacing, setReplacing] = useState<Record<string, boolean>>({});

  /**
   * Everything typed or chosen but not yet saved.
   *
   * One object rather than per-field state, so the Save button can say how
   * many changes are waiting and send exactly those and nothing else.
   */
  const [pending, setPending] = useState<Record<string, unknown>>({});

  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.settings() });
  const readiness = useQuery({
    queryKey: ['mpesa-readiness'],
    queryFn: () => api.mpesaReadiness(),
  });

  const mpesa = settings.data?.mpesa;

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateSettings({ mpesa: patch } as never),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      setReplacing({});
      setPending({});
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['mpesa-readiness'] });
    },
    onError: (e: Error) => {
      setSaved(false);
      setError(e.message);
    },
  });

  /** Put a change in the draft. Nothing leaves the page until Save. */
  const edit = (patch: Record<string, unknown>) => {
    setSaved(false);
    setPending((current) => ({ ...current, ...patch }));
  };

  /** The value to show: what has been typed, or what is stored. */
  const shown = <T,>(field: string, stored: T): T =>
    (field in pending ? (pending[field] as T) : stored);

  if (settings.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading the M-Pesa settings
      </p>
    );
  }

  if (!mpesa) return null;

  const ready = readiness.data?.configured === true;
  const sandbox = shown('environment', mpesa.environment) === 'sandbox';
  const changes = Object.keys(pending).length;

  /**
   * Send only what changed.
   *
   * A secret the admin did not type is not in `pending` at all, so it cannot
   * be sent as its mask — the server would ignore a mask anyway, but not
   * sending one is one fewer place for that rule to be got wrong.
   */
  const commit = () => {
    const patch: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(pending)) {
      /*
       * A secret that was typed and then emptied is a no-op, not a removal.
       * Clearing a credential is what "Remove it" is for, and it asks first.
       */
      if ((field === 'consumerSecret' || field === 'passkey') && String(value).trim() === '') {
        continue;
      }
      patch[field] = typeof value === 'string' ? value.trim() : value;
    }
    if (Object.keys(patch).length === 0) {
      setPending({});
      return;
    }
    save.mutate(patch);
  };

  return (
    <section className="card space-y-4" data-mpesa-settings>
      <div>
        <h2 className="flex items-center gap-2 font-heading text-lg">
          <Smartphone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          M-Pesa Express (Daraja)
          <HelpHint>
            The credentials that let this system raise a payment prompt on a handset. The consumer
            secret and the passkey are encrypted before they are stored and are never sent back to
            this page — you will see a mask, not the value.
          </HelpHint>
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          From a Daraja app at developer.safaricom.co.ke with the{' '}
          <strong className="text-foreground">M-PESA EXPRESS</strong> product on it. This is what
          raises the prompt that appears on a handset with the amount already in it. Takings go
          straight to your own shortcode and never pass through this system.
        </p>

        {/*
          * Where the four values actually come from, because two of them are
          * not where people look for them.
          *
          * A sandbox app card shows the consumer key and secret and then says
          * "Passkey: N/A" and "Short Code: N/A" — which reads as "this app has
          * none". It has both; they are on the Test Credentials page instead,
          * shared by every sandbox app. Somebody staring at N/A has no way to
          * know that, and stops there.
          */}
        {sandbox && (
          <p className="mt-2 rounded-xl border border-border bg-muted p-3 text-xs text-muted-foreground">
            <strong className="text-foreground">
              Your app card says Passkey: N/A and Short Code: N/A — that is normal.
            </strong>{' '}
            A sandbox app does not carry its own; every sandbox app shares one pair, and they are on{' '}
            <strong className="text-foreground">Test Credentials</strong> in the Daraja sidebar, not
            on the app card. The shortcode there is{' '}
            <span className="font-mono">{SANDBOX_SHORTCODE}</span>.
          </p>
        )}
      </div>

      {/* ── Where it stands ────────────────────────────────────────────────── */}
      {readiness.data && (
        <p
          className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${
            ready
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {ready ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {readiness.data.message}
        </p>
      )}

      {saved && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Saved.
        </p>
      )}
      {error && (
        <p className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      {/* ── The switch ─────────────────────────────────────────────────────── */}
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={shown('enabled', mpesa.enabled)}
          disabled={save.isPending}
          onChange={(event) => edit({ enabled: event.target.checked })}
        />
        <span>
          <span className="font-medium">Collect payments by M-Pesa</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Separate from having credentials, so collection can be switched off in a hurry without
            anybody having to find and delete a secret first.
          </span>
        </span>
      </label>

      {/*
        ── The four, in the order the Daraja app card lists them ──────────────

        Numbered, because the whole job is copying four values from another tab
        without losing your place.
      */}
      <div className="space-y-4 border-t border-border pt-4">
        <h3 className="text-sm font-medium">
          The four values from your Daraja app
          <span className="ml-2 font-normal text-muted-foreground">
            in the order the app card lists them
          </span>
        </h3>

        <PlainField
          step={1}
          label="Consumer Key"
          hint="On the app card in Daraja. Not a secret."
          value={String(shown('consumerKey', mpesa.consumerKey ?? ''))}
          placeholder="GkR9x…"
          disabled={save.isPending}
          onChange={(value) => edit({ consumerKey: value })}
        />

        <SecretField
          step={2}
          label="Consumer Secret"
          hint="On the app card in Daraja."
          stored={mpesa.consumerSecret}
          replacing={replacing.consumerSecret === true}
          value={String(pending.consumerSecret ?? '')}
          disabled={save.isPending}
          onStartReplacing={() => setReplacing((current) => ({ ...current, consumerSecret: true }))}
          onCancel={() => {
            setReplacing((current) => ({ ...current, consumerSecret: false }));
            setPending(({ consumerSecret: _drop, ...rest }) => rest);
          }}
          onChange={(value) => edit({ consumerSecret: value })}
          onRemove={() => save.mutate({ consumerSecret: null })}
        />

        <SecretField
          step={3}
          label="Passkey"
          hint={
            sandbox
              ? 'Your app card says N/A — take it from Test Credentials in the Daraja sidebar.'
              : 'Issued with the shortcode when Lipa na M-Pesa Online is enabled.'
          }
          stored={mpesa.passkey}
          replacing={replacing.passkey === true}
          value={String(pending.passkey ?? '')}
          disabled={save.isPending}
          onStartReplacing={() => setReplacing((current) => ({ ...current, passkey: true }))}
          onCancel={() => {
            setReplacing((current) => ({ ...current, passkey: false }));
            setPending(({ passkey: _drop, ...rest }) => rest);
          }}
          onChange={(value) => edit({ passkey: value })}
          onRemove={() => save.mutate({ passkey: null })}
        />

        <PlainField
          step={4}
          label="Short Code"
          hint={
            sandbox
              ? `Your app card says N/A — every sandbox app uses ${SANDBOX_SHORTCODE}, from Test Credentials.`
              : 'The paybill or till number customers already pay to.'
          }
          value={String(shown('shortcode', mpesa.shortcode ?? ''))}
          placeholder={SANDBOX_SHORTCODE}
          disabled={save.isPending}
          onChange={(value) => edit({ shortcode: value })}
        />
      </div>

      {/* ── The two choices, after the four values ─────────────────────────── */}
      <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
        <label className="text-sm">
          Which Safaricom
          <select
            className="input mt-1 w-full"
            value={String(shown('environment', mpesa.environment))}
            disabled={save.isPending}
            onChange={(event) => edit({ environment: event.target.value })}
          >
            <option value="sandbox">Sandbox — for testing</option>
            <option value="production">Live — real money</option>
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">
            Sandbox credentials do not work against live, and the other way round.
          </span>
        </label>

        <label className="text-sm">
          Shortcode type
          <select
            className="input mt-1 w-full"
            value={String(shown('shortcodeType', mpesa.shortcodeType))}
            disabled={save.isPending}
            onChange={(event) => edit({ shortcodeType: event.target.value })}
          >
            <option value="paybill">Paybill</option>
            <option value="buygoods">Buy Goods (till)</option>
          </select>
          <span className="mt-1 block text-xs text-muted-foreground">
            Getting this wrong returns an error that does not mention the mismatch.
          </span>
        </label>
      </div>

      {/* ── Save ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending || changes === 0}
          /*
           * A disabled button has to say why. "Nothing to save" is short, true,
           * and stops somebody pressing at a dim control wondering what is wrong
           * with the page.
           */
          title={changes === 0 ? 'Nothing has changed yet.' : `Save ${changes} change(s)`}
          onClick={commit}
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save changes
        </button>

        {changes > 0 && (
          <>
            <span className="text-sm text-amber-700">
              {changes} unsaved change{changes === 1 ? '' : 's'}.
            </span>
            <button
              type="button"
              className="btn-quiet"
              disabled={save.isPending}
              onClick={() => {
                setPending({});
                setReplacing({});
                setSaved(false);
              }}
            >
              Discard
            </button>
          </>
        )}
      </div>

      {/*
        The deployment detail that would otherwise be found the expensive way:
        a callback that cannot be reached means the prompt succeeds, the
        customer pays, and the confirmation never arrives.
      */}
      <p className="rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <KeyRound className="mr-1 inline h-3 w-3" aria-hidden="true" />
        Safaricom posts the confirmation to{' '}
        <code className="rounded bg-muted px-1">/api/v1/payments/mpesa/callback</code> on this
        server&rsquo;s public address, and to{' '}
        <code className="rounded bg-muted px-1">/api/v1/power/mpesa/callback</code> for a KPLC token
        purchase, each followed by a secret this server adds to every prompt. If those are not
        reachable from the internet the prompt still succeeds and the customer still pays — and
        nothing is ever marked as paid. A confirmation is checked back with Safaricom before
        anything is credited.
      </p>
    </section>
  );
}

/** A value that is not secret: shown in full, because checking it matters. */
function PlainField({
  step,
  label,
  hint,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  step: number;
  label: string;
  hint: string;
  value: string;
  placeholder: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="flex items-center gap-2 font-medium">
        <StepMark step={step} />
        {label}
      </span>
      <input
        className="input mt-1 w-full font-mono"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>
    </label>
  );
}

/**
 * A credential.
 *
 * Three states, and they are genuinely different things:
 *
 *   nothing stored  — an empty box to type into
 *   stored          — a mask, a "Change it" button and a "Remove it" button
 *   replacing       — a box, with cancel
 *
 * The middle state is the one that matters. It is what stops an admin editing
 * some other field on this page and silently replacing a working credential
 * with a row of bullet characters.
 *
 * The buttons sit under the field rather than floating to the right of it.
 * Beside a full-width input they ended up against the far edge of a wide
 * screen, a long way from the thing they act on, which read as though they
 * belonged to the page rather than the field.
 */
function SecretField({
  step,
  label,
  hint,
  stored,
  replacing,
  value,
  disabled,
  onStartReplacing,
  onCancel,
  onChange,
  onRemove,
}: {
  step: number;
  label: string;
  hint: string;
  stored: string | null;
  replacing: boolean;
  value: string;
  disabled: boolean;
  onStartReplacing: () => void;
  onCancel: () => void;
  onChange: (value: string) => void;
  onRemove: () => void;
}) {
  const has = Boolean(stored);

  if (has && !replacing) {
    return (
      <div className="text-sm">
        <span className="flex items-center gap-2 font-medium">
          <StepMark step={step} done />
          {label}
        </span>
        <input
          className="input mt-1 w-full font-mono"
          value={stored ?? ''}
          disabled
          readOnly
          aria-label={label}
        />
        <span className="mt-1 block text-xs text-muted-foreground">
          Stored and encrypted, and never sent back to this page. {hint}
        </span>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-quiet px-2 py-1 text-xs"
            disabled={disabled}
            onClick={onStartReplacing}
          >
            Change it
          </button>
          <button
            type="button"
            className="btn-quiet px-2 py-1 text-xs text-danger-700"
            disabled={disabled}
            onClick={onRemove}
            title="Delete this credential. Collection stops until a new one is saved."
          >
            Remove it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm">
      <span className="flex items-center gap-2 font-medium">
        <StepMark step={step} />
        {label}
      </span>
      <input
        className="input mt-1 w-full font-mono"
        type="password"
        autoComplete="off"
        value={value}
        disabled={disabled}
        aria-label={label}
        placeholder={has ? 'Type the new one' : 'Paste it here'}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>
      {has && (
        <div className="mt-2">
          <button
            type="button"
            className="btn-quiet px-2 py-1 text-xs"
            disabled={disabled}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** The number beside a field, so four values keep their order while copied. */
function StepMark({ step, done = false }: { step: number; done?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ${
        done ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'
      }`}
    >
      {step}
    </span>
  );
}
